import React, { useState, useEffect } from 'react';
import { useAuth, OAuthProvider } from '../../contexts/AuthContext';
import Portal from '../Portal';

export interface AuthModalProps {
  onClose: () => void;
  onEmailSent: (email: string) => void;
  actionName: string;
  hideSignInToggleAndCancel?: boolean; // Hide sign-in toggle and cancel button (for /lander-1)
}

const oauthProviders: Array<{ provider: OAuthProvider; label: string; icon: JSX.Element }> = [
  {
    provider: 'google',
    label: 'Continue with Google',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="currentColor"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="currentColor"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="currentColor"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    ),
  },
];

export default function AuthModal({ onClose, onEmailSent, actionName, hideSignInToggleAndCancel = false }: AuthModalProps) {
  const { signInWithOAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    try {
      setError(null);
      setOauthLoading(provider);
      await signInWithOAuth(provider);
      // OAuth redirect will happen automatically via Supabase
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      setOauthLoading(null);
    }
  };

  const handleEmailContinue = async () => {
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const { getSupabaseClient } = await import('../../services/supabase');
      const supabase = getSupabaseClient();
      const redirectTo = `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: authMode === 'signup',
          emailRedirectTo: redirectTo,
        },
      });

      if (error) {
        setError(error.message || 'Failed to send verification code. Please try again.');
        setLoading(false);
        return;
      }

      // Success - close this modal and open OTP modal
      onEmailSent(email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
      setLoading(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto">
        <div className="bg-bg-panel border border-border rounded-modal p-4 sm:p-6 md:p-8 w-full max-w-[min(450px,100%)] my-auto">
          <h2 className="text-h1 font-medium text-white mb-3 sm:mb-4">
            {authMode === 'signup' ? 'Create An Account' : 'Sign In'}
          </h2>
          <p className="text-body text-text-muted mb-4 sm:mb-6">
            {authMode === 'signup'
              ? 'Welcome to GenAnything! Sign up to continue.'
              : 'Sign in to continue.'}
          </p>

          {/* OAuth Buttons */}
          <div className="space-y-2 sm:space-y-3">
            {oauthProviders.map(({ provider, label, icon }) => (
              <button
                key={provider}
                onClick={() => handleOAuthSignIn(provider)}
                disabled={oauthLoading !== null || loading}
                className="w-full bg-[#F9F7EA] text-[#0f1218] py-3 min-h-[44px] rounded-button text-body font-medium hover:bg-[#E7D7B1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3"
              >
                {oauthLoading === provider ? (
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <span className="text-black w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0">{icon}</span>
                )}
                <span>{label}</span>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 sm:gap-4 my-4 sm:my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-text-subtle text-body-sm">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Email Input */}
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label htmlFor="auth-email" className="block text-white text-label font-medium mb-2">
                Email address
              </label>
              <input
                id="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && email.trim() && validateEmail(email)) {
                    handleEmailContinue();
                  }
                }}
                placeholder="Enter your email address"
                className="w-full bg-bg-panel text-white text-body px-4 py-3 rounded-button border border-border focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-border-hover placeholder:text-text-subtle"
                disabled={oauthLoading !== null || loading}
              />
            </div>

            <button
              onClick={handleEmailContinue}
              disabled={loading || oauthLoading !== null || !email.trim() || !validateEmail(email)}
              className="w-full bg-[#F9F7EA] text-[#0f1218] px-4 sm:px-6 py-3 min-h-[44px] rounded-button text-body font-medium hover:bg-[#E7D7B1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>

            {/* Error Message */}
            {error && (
              <div className="text-red-400 text-body-sm text-center mt-2">
                {error}
              </div>
            )}

            {/* Mode Toggle - Hidden on /lander-1 */}
            {!hideSignInToggleAndCancel && (
              <div className="pt-1 sm:pt-2 text-center">
                {authMode === 'signup' ? (
                  <button
                    onClick={() => setAuthMode('signin')}
                    className="text-text-subtle hover:text-white text-body-sm transition-colors underline underline-offset-2 hover:underline-offset-4 cursor-pointer"
                  >
                    Already have an account? Sign in
                  </button>
                ) : (
                  <button
                    onClick={() => setAuthMode('signup')}
                    className="text-text-subtle hover:text-white text-body-sm transition-colors underline underline-offset-2 hover:underline-offset-4 cursor-pointer"
                  >
                    Don&apos;t have an account? Sign up
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Cancel Button - Hidden on /lander-1 */}
          {!hideSignInToggleAndCancel && (
            <button
              onClick={onClose}
              className="w-full mt-4 sm:mt-6 text-text-subtle hover:text-white transition-colors text-body-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </Portal>
  );
}

